import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'bb',
  name: 'Bollinger Bands',
  shortName: 'BB',
  category: 'volatility',
  overlay: true,
  params: [
    { key: 'period', label: 'Period', type: 'number', default: 20, min: 2, max: 500 },
    {
      key: 'multiplier',
      label: 'Multiplier',
      type: 'number',
      default: 2,
      min: 0.1,
      max: 10,
      step: 0.1,
    },
  ],
  lines: [
    { key: 'upper', label: 'Upper', color: 'rgba(33,150,243,0.8)', width: 1 },
    { key: 'middle', label: 'Middle', color: '#2962ff', width: 1, style: 'dashed' },
    { key: 'lower', label: 'Lower', color: 'rgba(33,150,243,0.8)', width: 1 },
  ],
}

class BollingerBands extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(2, Number(params['period']) || 20)
    const mult = Number(params['multiplier']) || 2

    const upper: (number | null)[] = new Array<number | null>(candles.length).fill(null)
    const middle: (number | null)[] = new Array<number | null>(candles.length).fill(null)
    const lower: (number | null)[] = new Array<number | null>(candles.length).fill(null)

    // Reuse a sliding window sum and sum-of-squares for O(n) std-dev
    let sum = 0
    let sumSq = 0

    for (let i = 0; i < candles.length; i++) {
      const c = candles[i].c
      sum += c
      sumSq += c * c

      if (i >= period) {
        const old = candles[i - period].c
        sum -= old
        sumSq -= old * old
      }

      if (i >= period - 1) {
        const avg = sum / period
        const variance = sumSq / period - avg * avg
        const std = Math.sqrt(Math.max(0, variance))
        middle[i] = avg
        upper[i] = avg + mult * std
        lower[i] = avg - mult * std
      }
    }

    return {
      lines: [
        { key: 'upper', values: upper },
        { key: 'middle', values: middle },
        { key: 'lower', values: lower },
      ],
    }
  }
}

registerIndicator(BollingerBands, CONFIG)
